import { useEffect, useState } from "react";
import { trackConversion } from "@/lib/analytics";

/**
 * Client-side A/B bucketing. Assignment persists per-browser in localStorage
 * and fires once via GA4 (experiment_view) on first resolution.
 *
 * Returns null until resolved (SSG-rendered HTML and the first client paint),
 * so treat null as "don't show either variant yet" to avoid a flash.
 *
 * Preview override: append `?<experimentId>=<variant>` to the URL to force a
 * bucket for manual QA. Overrides don't touch localStorage and don't fire the
 * tracking event, so previewing never pollutes real experiment data.
 */
export function useExperiment(
  experimentId: string,
  variants: [string, string] = ["control", "variant"],
) {
  const [variant, setVariant] = useState<string | null>(null);

  useEffect(() => {
    const override = new URLSearchParams(window.location.search).get(experimentId);
    if (override && variants.includes(override)) {
      setVariant(override);
      return;
    }

    const key = `meshly_exp_${experimentId}`;
    let assigned = window.localStorage.getItem(key);

    if (!assigned || !variants.includes(assigned)) {
      assigned = variants[Math.random() < 0.5 ? 0 : 1];
      window.localStorage.setItem(key, assigned);
    }

    setVariant(assigned);
    trackConversion("experiment_view", {
      experiment_id: experimentId,
      variant_id: assigned,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experimentId]);

  return variant;
}
